import { useEffect, useRef, useState } from 'react';
import { shrinkPhoto, type AssistClient, type ScanFields } from '../assist/client';
import { quotaMessage, t } from '../i18n';
import { CameraCapture, canUseCamera } from './CameraCapture';

/**
 * "Scanner la bouteille" (T126, spec.md User Story 8; renamed 2026-10-06): takes a photo (the
 * camera on a phone), shrinks it, asks the `assist` function to read it, and hands the recognised
 * fields to the page, which pre-fills the form. Owner request 2026-10-08: the front and the back
 * labels both say something, so after the front photo it offers "Photographier le dos" or "Lire
 * cette photo seule"; both photos are read together, in one request. Owner request 2026-10-14:
 * one tap opens the camera inside the app for both photos, the front then the back
 * (`CameraCapture`); the phone's own camera, one photo at a time, is kept for when that camera
 * cannot be opened. Nothing is saved here. A failure says so and leaves the form as it is.
 */
export function LabelScanControl({
  client,
  onDraft,
  onPhotos,
}: {
  client: AssistClient;
  onDraft: (fields: ScanFields) => void;
  /** The photos read (front, then back), to keep with the wine (owner request, 2026-10-11). */
  onPhotos?: (images: string[]) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; failed: boolean } | undefined>();
  // The front photo, shrunk, while the person decides whether to add the back.
  const [front, setFront] = useState<{ image: string; preview: string } | undefined>();
  const [camera, setCamera] = useState(false);
  // No camera inside the app (refused, none): the phone's own camera, one photo at a time.
  const [phoneCamera, setPhoneCamera] = useState(() => !canUseCamera());

  useEffect(() => () => (front ? URL.revokeObjectURL(front.preview) : undefined), [front]);

  async function read(images: string[]) {
    onPhotos?.(images);
    setBusy(true);
    setMessage(undefined);
    try {
      const result = await client.scan(images);
      if (result.ok) {
        onDraft(result.fields);
        setMessage({ text: t('scan.done'), failed: false });
      } else {
        setMessage({
          text:
            result.reason === 'quota'
              ? quotaMessage(result.period)
              : t(result.reason === 'unreadable' ? 'scan.failed' : 'scan.unavailable'),
          failed: true,
        });
      }
    } finally {
      setBusy(false);
    }
  }

  async function took(file: File) {
    setMessage(undefined);
    let image: string;
    try {
      image = await shrinkPhoto(file);
    } catch {
      // A photo the browser cannot open.
      setMessage({ text: t('scan.failed'), failed: true });
      return;
    }
    if (!front) {
      setFront({ image, preview: URL.createObjectURL(file) });
      return;
    }
    const images = [front.image, image];
    setFront(undefined);
    await read(images);
  }

  async function tookBoth(photos: Blob[]) {
    setCamera(false);
    setMessage(undefined);
    let images: string[];
    try {
      images = await Promise.all(photos.map((photo) => shrinkPhoto(photo)));
    } catch {
      setMessage({ text: t('scan.failed'), failed: true });
      return;
    }
    await read(images);
  }

  function start() {
    setMessage(undefined);
    if (phoneCamera) input.current?.click();
    else setCamera(true);
  }

  function readFrontOnly() {
    if (!front) return;
    const images = [front.image];
    setFront(undefined);
    void read(images);
  }

  return (
    <div className="label-scan">
      <button
        type="button"
        className="cta-action"
        disabled={busy || front !== undefined}
        onClick={start}
      >
        {busy ? t('scan.reading') : t('scan.action')}
      </button>
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        aria-label={front ? t('scan.addBack') : t('scan.action')}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) void took(file);
        }}
      />
      {camera && (
        <CameraCapture
          onDone={(photos) => void tookBoth(photos)}
          onCancel={() => setCamera(false)}
          onUnavailable={() => {
            setCamera(false);
            setPhoneCamera(true);
            setMessage({ text: t('camera.unavailable'), failed: true });
          }}
        />
      )}
      {front && (
        <div className="label-scan__back" role="group" aria-label={t('scan.frontTaken')}>
          <img src={front.preview} alt={t('scan.frontPhoto')} className="label-scan__preview" />
          <p className="label-scan__message">{t('scan.frontTaken')}</p>
          <div className="label-scan__actions">
            <button type="button" className="cta-action" onClick={() => input.current?.click()}>
              {t('scan.addBack')}
            </button>
            <button type="button" className="cta-action" onClick={readFrontOnly}>
              {t('scan.frontOnly')}
            </button>
          </div>
        </div>
      )}
      {message && (
        <p role={message.failed ? 'alert' : 'status'} className="label-scan__message">
          {message.text}
        </p>
      )}
    </div>
  );
}
